import { OWNER_EMAILS, ROLES, ROLES_ASIGNABLES, ROLES_DELEGABLES } from '../constants/roles';

/*
 * Con qué rol entra un usuario interno (@axoncapital) a la planilla. Es el mismo
 * orden que aplican las reglas de Firestore (isAdmin / isComercial):
 *   1. dueño → Admin, siempre;
 *   2. rol asignado por un admin (`rolesUsuarios/{email}` activo);
 *   3. empleado comercial activo en el SGC (`comercialesClientes/{email}`) → Comercial;
 *   4. nada de lo anterior → sin asignar: elige Trader o Middle Office (transición).
 */

export const ORIGEN_ROL = {
  DUENO: 'dueno',
  ASIGNADO: 'asignado',
  SGC: 'sgc',
  SIN_ASIGNAR: 'sinAsignar',
};

export const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

export const isOwnerEmail = (email) => OWNER_EMAILS.includes(normalizeEmail(email));

const rolAsignadoActivo = (rolAsignado) => (
  rolAsignado && rolAsignado.active === true && ROLES_ASIGNABLES.includes(rolAsignado.rol)
    ? rolAsignado.rol
    : null
);

/** Roles que el dueño le delegó a alguien con rol asignado (`asigna`). */
export const delegacionesDe = (rolAsignado) => (
  rolAsignadoActivo(rolAsignado) && Array.isArray(rolAsignado.asigna)
    ? ROLES_DELEGABLES.filter(rol => rolAsignado.asigna.includes(rol))
    : []
);

/**
 * @param {object} params
 * @param {string} params.email
 * @param {object|null} params.rolAsignado  doc de `rolesUsuarios` o null
 * @param {object|null} params.comercialSgc doc de `comercialesClientes` o null
 * @returns {{ email: string, rol: string|null, origen: string, esAdmin: boolean, esDueno: boolean, asigna: string[] }}
 *   `rol` null = sin asignar (elige en la pantalla de transición). `asigna`: roles
 *   que puede dar o quitar por delegación del dueño.
 */
export const resolveRolUsuario = ({ email, rolAsignado = null, comercialSgc = null }) => {
  const base = { email: normalizeEmail(email), esAdmin: false, esDueno: false, asigna: [] };
  if (isOwnerEmail(email)) {
    return { ...base, rol: ROLES.ADMIN, origen: ORIGEN_ROL.DUENO, esAdmin: true, esDueno: true };
  }
  const asignado = rolAsignadoActivo(rolAsignado);
  if (asignado) {
    return {
      ...base,
      rol: asignado,
      origen: ORIGEN_ROL.ASIGNADO,
      esAdmin: asignado === ROLES.ADMIN,
      asigna: delegacionesDe(rolAsignado),
    };
  }
  if (comercialSgc && comercialSgc.active !== false) {
    return { ...base, rol: ROLES.COMERCIAL, origen: ORIGEN_ROL.SGC };
  }
  return { ...base, rol: null, origen: ORIGEN_ROL.SIN_ASIGNAR };
};

/** Ve el panel Usuarios: admins y quien tiene delegación para asignar roles. */
export const veUsuarios = (rolUsuario) => !!rolUsuario && (rolUsuario.esAdmin || (rolUsuario.asigna || []).length > 0);

/**
 * Rol con el que se opera en las pestañas. Un admin actúa como Admin salvo que
 * haya elegido otro desde el encabezado; quien no tiene rol usa el que eligió.
 */
export const resolveRolOperativo = ({ rolUsuario, rolElegido }) => {
  if (!rolUsuario) return null;
  if (rolUsuario.esAdmin) return rolElegido || ROLES.ADMIN;
  if (rolUsuario.rol) return rolUsuario.rol;
  return rolElegido || null;
};

/**
 * Comercial con el que arranca una orden nueva: el propio usuario si opera como
 * Comercial o como Admin (el admin lo puede cambiar); el resto elige por cuenta de
 * qué comercial carga.
 */
export const comercialPorDefecto = ({ userRole, userName }) => (
  [ROLES.COMERCIAL, ROLES.ADMIN, ROLES.SUPER_ADMIN].includes(userRole) ? userName || '' : ''
);

/**
 * Quién puede dar o quitar un rol desde el panel (`rolNuevo` null = quitar). Mismo
 * criterio que las reglas de `rolesUsuarios`:
 * - el dueño, todo (es el único que toca el rol Admin y las delegaciones);
 * - un admin, todo menos Admin;
 * - un delegado, solo sus roles delegados, a quien no tiene rol o tiene uno de
 *   esos, y nunca a sí mismo.
 */
export const puedeAsignarRol = ({ rolUsuario, rolActual, rolNuevo, email = null }) => {
  if (!rolUsuario) return false;
  if (rolUsuario.esDueno) return true;
  if (rolUsuario.esAdmin) return rolNuevo !== ROLES.ADMIN && rolActual !== ROLES.ADMIN;
  const delegados = rolUsuario.asigna || [];
  if (delegados.length === 0) return false;
  if (email && normalizeEmail(email) === rolUsuario.email) return false;
  return (rolNuevo === null || delegados.includes(rolNuevo))
    && (!rolActual || delegados.includes(rolActual));
};

const ORDEN_ORIGEN = {
  [ORIGEN_ROL.SIN_ASIGNAR]: 0,
  [ORIGEN_ROL.DUENO]: 1,
  [ORIGEN_ROL.ASIGNADO]: 2,
  [ORIGEN_ROL.SGC]: 3,
};

// Sin rol y sin ingresos (p. ej. a quien se le quitó el rol y nunca entró): no es
// un pendiente, va al final.
const ordenFila = (fila) => (
  fila.origen === ORIGEN_ROL.SIN_ASIGNAR && !fila.ultimoLoginAt ? 4 : ORDEN_ORIGEN[fila.origen]
);

/**
 * Filas del panel Usuarios: une los roles asignados, los comerciales activos del
 * SGC y los últimos ingresos, una fila por email, con el rol que resulta de
 * aplicar resolveRolUsuario. Primero los que entraron sin rol asignado.
 */
export const armarFilasUsuarios = ({ roles = [], accesos = [], comerciales = [] }) => {
  const porEmail = new Map();
  const fila = (email) => {
    const id = normalizeEmail(email);
    if (!id) return null;
    if (!porEmail.has(id)) {
      porEmail.set(id, { email: id, nombre: '', rolDoc: null, comercialSgc: null, acceso: null });
    }
    return porEmail.get(id);
  };

  roles.forEach((doc) => {
    const f = fila(doc.id || doc.email);
    if (f) f.rolDoc = doc;
  });
  comerciales.filter(c => c.active !== false).forEach((doc) => {
    const f = fila(doc.id || doc.email);
    if (f) f.comercialSgc = doc;
  });
  accesos.forEach((doc) => {
    const f = fila(doc.id || doc.email);
    if (f) f.acceso = doc;
  });
  OWNER_EMAILS.forEach(email => fila(email));

  return [...porEmail.values()]
    .map((f) => {
      const rol = resolveRolUsuario({ email: f.email, rolAsignado: f.rolDoc, comercialSgc: f.comercialSgc });
      return {
        ...f,
        nombre: f.rolDoc?.nombre || f.acceso?.nombre || f.comercialSgc?.nombre || '',
        rolAsignado: rol.origen === ORIGEN_ROL.ASIGNADO ? rol.rol : null,
        rolEfectivo: rol.rol,
        origen: rol.origen,
        rolUsado: f.acceso?.rolUsado || null,
        ultimoLoginAt: f.acceso?.ultimoLoginAt || null,
      };
    })
    .sort((a, b) => (ordenFila(a) - ordenFila(b))
      || (a.nombre || a.email).localeCompare(b.nombre || b.email, 'es'));
};
